import { partytownSnippet } from '@qwik.dev/partytown/integration';

// initialize analytics once and keep partytown running across livewire navigation.
// track page changes without reloading analytics scripts or blocking navigation.

// set up analytics inside the partytown worker.
export function initializeWorkerAnalytics(measurementId) {
    window.dataLayer = window.dataLayer || [];
    // queue analytics commands.
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', measurementId);

    const pending = [];
    // send queued page views when analytics is ready.
    window.nriFlushAhrefsViews = () => {
        const script = document.querySelector('script[data-nri-ahrefs]:not([type^="text/partytown"])')
            || document.querySelector('script[data-nri-ahrefs]');
        if (!script || !window.AhrefsAnalytics?.sendEvent) return;

        const currentTitle = document.title;
        try {
            while (pending.length) {
                const view = pending.shift();
                script.setAttribute('data-page-location', view.url);
                document.title = view.title;
                window.AhrefsAnalytics.sendEvent('pageview');
            }
        } finally {
            script.removeAttribute('data-page-location');
            document.title = currentTitle;
        }
    };
    // queue page views received from the browser.
    window.nriTrackAhrefsPage = view => {
        pending.push(view);
        window.nriFlushAhrefsViews();
    };
}

// start analytics once and track completed page navigation.
export function initializeAnalytics() {
    const settings = document.querySelector('meta[name="nri-analytics"]');
    if (!settings || window.__nriAnalyticsInitialized) return;

    window.__nriAnalyticsInitialized = true;
    let ready = false;
    let ahrefsScript = null;
    let previousUrl = null;
    const pendingViews = [];

    // load scripts through partytown.
    const appendWorkerScript = (content, src = '') => {
        const script = document.createElement('script');
        script.type = 'text/partytown';
        if (src) script.src = src;
        else script.textContent = content;
        document.head.appendChild(script);
        return script;
    };

    // forward queued page views to the worker.
    const flushViews = () => {
        if (ready) {
            while (pendingViews.length) {
                window.nriTrackAhrefsPage(pendingViews.shift());
            }
        }
    };

    // send queued views when partytown is ready.
    document.addEventListener('pt0', () => {
        ready = true;
        flushViews();
    }, { once: true });

    // preserve analytics settings when partytown falls back to the main thread.
    new MutationObserver(records => {
        for (const record of records) {
            for (const script of record.addedNodes) {
                if (script.tagName !== 'SCRIPT' || script.type.includes('partytown')
                    || script.src !== ahrefsScript?.src) continue;

                for (const attribute of ahrefsScript.attributes) {
                    if (attribute.name.startsWith('data-')) {
                        script.setAttribute(attribute.name, attribute.value);
                    }
                }
                // resume tracking when fallback analytics is ready.
                script.addEventListener('load', () => {
                    ready = true;
                    flushViews();
                    window.nriFlushAhrefsViews?.();
                });
            }
        }
    }).observe(document.head, { childList: true });

    appendWorkerScript(`(${initializeWorkerAnalytics.toString()})(${JSON.stringify(settings.content)});`);
    appendWorkerScript('', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(settings.content)}`);

    // track page changes while respecting exclusions and avoiding duplicates.
    const trackPage = () => {
        const url = window.location.href;
        if (url === previousUrl) return;
        previousUrl = url;

        const key = document.querySelector('meta[name="nri-analytics"]')?.dataset.ahrefsKey;
        if (!key) return;
        pendingViews.push({ url, title: document.title });

        if (!ahrefsScript) {
            ahrefsScript = document.createElement('script');
            ahrefsScript.type = 'text/partytown';
            ahrefsScript.src = 'https://analytics.ahrefs.com/analytics.js';
            ahrefsScript.setAttribute('data-nri-ahrefs', '');
            ahrefsScript.setAttribute('data-key', key);
            ahrefsScript.setAttribute('data-no-pageview-on-load', '');
            ahrefsScript.setAttribute('data-no-pageview-auto', '');
            document.head.appendChild(ahrefsScript);
            appendWorkerScript('window.nriFlushAhrefsViews();');
            window.dispatchEvent(new CustomEvent('ptupdate'));
        }
        flushViews();
    };

    // start partytown independently of livewire's page updates.
    const bootstrap = document.createElement('script');
    bootstrap.textContent = partytownSnippet({
        lib: '/~partytown/',
        forward: ['gtag', 'dataLayer.push', 'nriTrackAhrefsPage'],
        strictProxyHas: true,
        // keep the runtime outside the page content replaced during navigation.
        sandboxParent: 'html',
    });
    document.head.appendChild(bootstrap);

    document.addEventListener('livewire:navigated', trackPage);
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', trackPage, { once: true });
    } else {
        trackPage();
    }
}
